import { checkboxClasses } from '@mui/material/Checkbox';
import { iconButtonClasses } from '@mui/material/IconButton';
import { listClasses } from '@mui/material/List';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { menuItemClasses } from '@mui/material/MenuItem';
import { paperClasses } from '@mui/material/Paper';
import { alpha, darken, lighten, Theme } from '@mui/material/styles';
import { tablePaginationClasses } from '@mui/material/TablePagination';
import { gridClasses } from '@mui/x-data-grid';
import type { DataGridComponents } from '@mui/x-data-grid/themeAugmentation';

const getBackgroundColor = (
  color: string,
  theme: Theme,
  coefficient: number,
) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles('light', {
    backgroundColor: lighten(color, coefficient),
  }),
});

export const DataGrid: DataGridComponents<Theme> = {
  MuiDataGrid: {
    defaultProps: {
      density: 'compact',
      disableRowSelectionOnClick: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        overflow: 'auto',
        border: 'none',
        boxShadow: theme.customShadows.card,
        backgroundColor: theme.palette.background.default,
        '& .plot-boundary-status--false': {
          ...getBackgroundColor(theme.palette.info.light, theme, 0.7),
          '&:hover': {
            ...getBackgroundColor(theme.palette.info.light, theme, 0.6),
          },
          '&.Mui-selected': {
            ...getBackgroundColor(theme.palette.info.light, theme, 0.5),
            '&:hover': {
              ...getBackgroundColor(theme.palette.info.light, theme, 0.4),
            },
          },
        },
        '& .plot-status-2': {
          backgroundColor: theme.palette.grey[300],
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        },
        '& .file-status-1': {
          backgroundColor: theme.palette.grey[300],
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        },
        '& .company-status-2': {
          backgroundColor: theme.palette.grey[300],
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        },
        '& .user-account-status-2': {
          backgroundColor: theme.palette.grey[300],
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '--DataGrid-overlayHeight': '300px',
        [`& .${gridClasses.columnHeader}`]: {
          backgroundColor: theme.palette.background.neutral,
          minHeight: 50,
        },
        [`& .MuiDataGrid-columnSeparator`]: {
          color: theme.palette.secondary.lighter,
        },

        [`& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)`]:
          {
            color: 'transparent',
          },
        [`& .${gridClasses.footerContainer}`]: {
          backgroundColor: theme.palette.background.paper,
        },
        [`& .${checkboxClasses.root}`]: {
          padding: theme.spacing(2),
          '& > svg': {
            fontSize: '1.2rem',
          },
        },
        [`& .${tablePaginationClasses.root}`]: {
          marginRight: theme.spacing(1),
          '& .MuiIconButton-root': {
            maxHeight: 32,
            maxWidth: 32,
            '& > svg': {
              fontSize: '1rem',
            },
          },
        },
      }),
      cell: ({ theme }) => ({
        borderTopColor: theme.palette.divider,
        '&:hover': {
          cursor: 'default',
        },
      }),
      menu: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        backgroundImage: 'none',
        [`& .${paperClasses.root}`]: {
          border: `1px solid ${theme.palette.divider}`,
        },

        [`& .${menuItemClasses.root}`]: {
          margin: '0 4px',
        },
        [`& .${listItemIconClasses.root}`]: {
          marginRight: 0,
        },
        [`& .${listClasses.root}`]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }),

      row: ({ theme }) => ({
        '&:last-of-type': {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.light,
        },
        '&.Mui-selected': {
          background: theme.palette.primary.light,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }),
      iconButtonContainer: ({ theme }) => ({
        [`& .${iconButtonClasses.root}`]: {
          border: 'none',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
          '&:active': {
            backgroundColor: theme.palette.grey[200],
          },
          ...theme.applyStyles('dark', {
            color: theme.palette.grey[50],
            '&:hover': {
              backgroundColor: theme.palette.grey[800],
            },
            '&:active': {
              backgroundColor: theme.palette.grey[900],
            },
          }),
        },
      }),
      menuIconButton: ({ theme }) => ({
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.grey[100],
        },
        '&:active': {
          backgroundColor: theme.palette.grey[200],
        },
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[50],
          '&:hover': {
            backgroundColor: theme.palette.grey[800],
          },
          '&:active': {
            backgroundColor: theme.palette.grey[900],
          },
        }),
      }),
      filterForm: ({ theme }) => ({
        gap: theme.spacing(1),
        alignItems: 'flex-end',
      }),
      columnsManagementHeader: ({ theme }) => ({
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
      }),
      columnHeaderTitleContainer: {
        flexGrow: 1,
        justifyContent: 'space-between',
      },
      columnHeaderDraggableContainer: { paddingRight: 2 },
    },
  },
};
